import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { List, X, User, SignOut } from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export function Header({ onContactClick }) {
  const { language, toggleLanguage, t } = useLanguage();
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isHomePage = location.pathname === '/';

  // For non-authenticated users on homepage, use anchor links
  // Otherwise use regular route links
  const navLinks = isAuthenticated ? [
    { path: '/dashboard', label: t('nav.dashboard'), isAnchor: false },
    { path: '/documents', label: t('nav.documents'), isAnchor: false },
  ] : [
    { path: isHomePage ? '#how-it-works' : '/#how-it-works', label: t('nav.howItWorks'), isAnchor: isHomePage },
    { path: isHomePage ? '#pricing' : '/#pricing', label: t('nav.pricing'), isAnchor: isHomePage },
    { path: isHomePage ? '#faq' : '/#faq', label: t('nav.faq'), isAnchor: isHomePage },
  ];

  const handleNavClick = (e, link) => {
    if (link.isAnchor) {
      e.preventDefault();
      const targetId = link.path.replace('#', '');
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      setMobileMenuOpen(false);
    } else if (link.path.startsWith('/#')) {
      // Navigate to home then scroll
      e.preventDefault();
      navigate('/');
      setTimeout(() => {
        const targetId = link.path.replace('/#', '');
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      setMobileMenuOpen(false);
    }
  };

  const isActiveSection = (path) => {
    if (path.startsWith('#') || path.startsWith('/#')) {
      return false; // Don't highlight anchor links
    }
    return location.pathname === path;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#E5E7EB]" data-testid="header" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <nav className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to={isAuthenticated ? '/dashboard' : '/'} 
            className="flex items-center gap-2.5"
            data-testid="logo-link"
          >
            <div className="w-9 h-9 bg-[#0F172A] rounded-xl flex items-center justify-center">
              <span className="text-white font-semibold text-lg">P</span>
            </div>
            <span className="font-semibold text-xl text-[#0F172A]">PaperPal</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.path}
                href={link.path}
                onClick={(e) => handleNavClick(e, link)}
                className={`text-sm font-medium transition-colors cursor-pointer ${
                  isActiveSection(link.path) 
                    ? 'text-[#0F172A]' 
                    : 'text-[#475569] hover:text-[#0F172A]'
                }`}
                data-testid={`nav-${link.path.replace(/[#\/]/g, '')}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right Side */}
          <div className="hidden md:flex items-center gap-4">
            {/* Language Toggle - Pill shaped */}
            <div 
              className="flex items-center bg-[#F9FAFB] border border-[#E5E7EB] rounded-full p-1"
              data-testid="language-toggle-container"
            >
              <button
                onClick={() => language !== 'en' && toggleLanguage()}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  language === 'en' 
                    ? 'bg-white text-[#0F172A] shadow-sm' 
                    : 'text-[#475569] hover:text-[#0F172A]'
                }`}
                data-testid="language-toggle-en"
              >
                EN
              </button>
              <button
                onClick={() => language !== 'fr' && toggleLanguage()}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  language === 'fr' 
                    ? 'bg-white text-[#0F172A] shadow-sm' 
                    : 'text-[#475569] hover:text-[#0F172A]'
                }`}
                data-testid="language-toggle-fr"
              >
                FR
              </button>
            </div>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="flex items-center gap-2.5 px-3 py-2 rounded-xl border border-[#E5E7EB] hover:bg-[#F9FAFB] transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    {user?.picture ? (
                      <img 
                        src={user.picture} 
                        alt={user.first_name} 
                        className="w-7 h-7 rounded-full"
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-[#0F172A]/10 flex items-center justify-center">
                        <span className="text-sm font-medium text-[#0F172A]">
                          {user?.first_name?.[0] || 'U'}
                        </span>
                      </div>
                    )}
                    <span className="text-sm font-medium text-[#0F172A]">
                      {user?.first_name || 'User'}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2" data-testid="profile-link">
                      <User size={16} />
                      {t('nav.profile')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="flex items-center gap-2 text-red-600"
                    data-testid="logout-button"
                  >
                    <SignOut size={16} />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm font-medium text-[#0F172A] hover:text-[#1E293B] transition-colors px-4 py-2 border border-[#E5E7EB] rounded-[10px] hover:bg-[#F9FAFB]"
                  data-testid="login-link"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/signup"
                  className="bg-[#0F172A] hover:bg-[#1E293B] text-white font-medium rounded-[12px] px-6 py-2.5 text-sm transition-colors"
                  data-testid="get-started-link"
                >
                  {t('nav.getStarted')}
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 md:hidden">
            {/* Mobile Language Toggle */}
            <div className="flex items-center bg-[#F9FAFB] border border-[#E5E7EB] rounded-full p-0.5">
              <button
                onClick={() => language !== 'en' && toggleLanguage()}
                data-testid="mobile-language-toggle-en"
                className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  language === 'en' 
                    ? 'bg-white text-[#0F172A] shadow-sm' 
                    : 'text-[#475569]'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => language !== 'fr' && toggleLanguage()}
                data-testid="mobile-language-toggle-fr"
                className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  language === 'fr' 
                    ? 'bg-white text-[#0F172A] shadow-sm' 
                    : 'text-[#475569]'
                }`}
              >
                FR
              </button>
            </div>
            
            {/* Mobile Sign in button - visible in header */}
            {!isAuthenticated && (
              <Link
                to="/login"
                className="text-xs font-medium text-[#0F172A] px-3 py-1.5 border border-[#E5E7EB] rounded-full hover:bg-[#F9FAFB] transition-colors"
                data-testid="mobile-login-link"
              >
                {t('nav.login')}
              </Link>
            )}
            
            <button
              className="p-2 rounded-xl hover:bg-[#F9FAFB] transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              type="button"
              aria-label={language === 'fr' ? (mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu') : (mobileMenuOpen ? 'Close menu' : 'Open menu')}
              aria-expanded={mobileMenuOpen}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? (
                <X size={24} className="text-[#0F172A]" />
              ) : (
                <List size={24} className="text-[#0F172A]" />
              )}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-6 border-t border-[#E5E7EB]" data-testid="mobile-menu">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <a
                  key={link.path}
                  href={link.path}
                  onClick={(e) => handleNavClick(e, link)}
                  className={`px-4 py-3.5 rounded-xl text-base font-medium transition-colors cursor-pointer ${
                    isActiveSection(link.path) 
                      ? 'bg-[#0F172A]/10 text-[#0F172A]' 
                      : 'text-[#475569] hover:bg-[#F9FAFB]'
                  }`}
                >
                  {link.label}
                </a>
              ))}
              
              <div className="border-t border-[#E5E7EB] my-3" />

              {isAuthenticated ? (
                <>
                  <Link
                    to="/profile"
                    className="px-4 py-3.5 text-base font-medium text-[#475569] rounded-xl hover:bg-[#F9FAFB]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('nav.profile')}
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="px-4 py-3.5 text-base font-medium text-red-600 text-left rounded-xl hover:bg-red-50"
                  >
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <Link
                  to="/signup"
                  className="mx-4 bg-[#0F172A] hover:bg-[#1E293B] text-white font-medium rounded-[12px] px-6 py-3 text-base text-center transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav.getStarted')}
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
